import * as Utils from './utils';
import * as Config from './config';
import { tracker as Analytics } from './analytics.js';

/**
 * Base Modal class. Uses Bootstrap modals.
 */
export class Modal {
    /**
     * @constructor
     * @constructs Modal
     * @param {object} Default = {}. Options for the modal.
     */
    constructor(options = {}) {
        this.id = "standardModal";
        this.modalHtml = `<div class="modal-content">
                            <div class="modal-header"></div>
                            <div class="modal-body"></div>
                            <div class="modal-footer">
                            </div>
                        </div>`;
        this.dismissible = options.dismissible ? options.dismissable : true;
        this.footerHtml = `<div class="button close-button">${Utils.i18n('modalConfirmation', 'OK')}</div>`;
    }

    /**
     * Opens the modal
     * 
     * @param {object} content - A plain javascript object containing the content for the modal
     * @param {selector} selector - A selector for the modal to open
     */
    openModal(content, selector) {
        this.createModal(selector);
        this.addModalContent(content, selector);
        $(selector).attr('tabIndex', '-1');
        // Timeout has to be set because shown.bs.modal event is not being emitted. Bootstrap animations take 300ms
        // to complete. Giving some padding to that.
        $(selector).on('show.bs.modal', () => {
            setTimeout(() => {
                $(selector).focus();
            }, 500);
        });
        $(selector).modal();
        $(selector).on('hidden.bs.modal', this.destroyModal.bind(this, selector));
        $('.modal-backdrop').on('click', () => { 
            if (this.dismissible) {
                $(selector).modal('hide');
            }
        });
    }

    /**
     * Adds content to the modal
     * 
     * @param {object} A plain javascript object containing content for the modal
     * @param {selector} selector - A selector for the modal to add this content to
     * @param {function} listeners - Any specific listeners to be added to the modal
     */
    addModalContent(content, selector, listeners) {
        let $modal = $(selector);
        let header = content.header;
        let body = content.body;
        let footer = content.footer || this.footerHtml;

        $modal.find('.modal-header').html(header);

        // tests whether or not the body content provided is an array of content, or just a string of content
        if (toString.call(body).match(/array/i)) {
            $modal.find('.modal-body').html(this.parseArrayToBody(body));
            $modal.find('.modal-body').css('padding', '30px');
        } else {
            $modal.find('.modal-body').html(body);
        }

        $modal.find('.modal-footer').html(footer);
        $modal.find('.close-button').on('click', () => {
            $modal.modal('hide');
        });
    }

    /**
     * Parses an array of content to a single string of paragraphs
     * 
     * @param {array} content - An array of content (e.g. paragraphs)
     * @returns {string} A string of content. Each item in the array that was passed to the function is wrapped with a
     * paragraph tag and given the class "modal-paragraph"
     */
    parseArrayToBody(content) {
        return content.reduce((collector, item) => {
            let entry = '';
            if (typeof item === "object") {
                entry = `<p class="modal-paragraph-title">${item.title}</p><p class="modal-paragraph">${item.content}</p>`;
            } else if (typeof item === "string") {
                entry = `<p class="modal-paragraph">${item}</p>`;
            }
            return collector = collector += entry;
        }, "");
    }

    /**
     * Creates the bootstrap modal
     * 
     * @param {selector} selector - A selector for the modal
     */
    createModal(selector) {
        let modal = document.createElement('div');
        modal.className = "modal fade";
        modal.id = this.id;
        $('#modal-container').append(modal);
        $(selector).html(this.modalHtml);
    }

    /**
     * Destroys the modal and any attached listeners
     * 
     * @param {selector} selector - A selector for the modal to be destroyed
     */
    destroyModal(selector) {
        $(selector).remove();
    }
}

/**
 * Class for modals showing details of a pharmacy and the selected drug
 */
export class DetailModal extends Modal {
    /**
     * @constructor
     * @constructs DetailModal
     * @param {object} options - A plain javascript with options
     */
    constructor(options) {
        super();
        this.id = "detailsModal";
        this.modalHtml = `<div class="modal-content">
                            <div class="sticky-content">
                                <div class="modal-header">
                                    <div class="provider-image"></div>
                                </div>
                                <div class="modal-body"></div>
                            </div>
                            <div class="sticky-footer">
                                <div class="modal-footer">
                            </div>
                        </div>`;
        if (options.drugData) {
            this.drugHtml = `
                <div class="drug-prices">
                    <div class="brand-name-prices"></div>
                    <div class="generic-prices"></div>
                    <br/>
                    <div class="dispensing-fee"></div>
                    ${Utils.generateButtonHtml('buyGeneric', Utils.i18n('buyGeneric'), 'no-logo')}
                </div>
                `;
        } else {
            this.drugHtml = ``;
        }

        this.bodyHtml = `
            <div id="button-row"></div>
            <div class="provider-name"></div>
            ${this.drugHtml}
            <div class="store-details">
                <div class="modal-subtitle">${Utils.i18n('location')}</div>
                <div class="address-details"></div>
                <div id="opening-hours" class="hours"></div>
            </div>`;
        this.footerHtml = '<div class="button footer-confirm"></div>';
        this.ppButtonContent = `
            <div class="po-title">${Utils.i18n('preferredProvider')}</div>
            <a class="po-link" href="${Config.PREFERRED_FAQ_LINK}" target="_blank">${Utils.i18n('readMore')}</a>`;
        this.saButtonContent = `
            <div class="po-title">${Utils.i18n('specialAuthority')}</div>
            <div class="po-text">${Utils.i18n('specialAuthorityDetails')}</div>
            <a class="po-link" href="${Config.SA_FAQ_LINK}" target="_blank">${Utils.i18n('readMore')}</a>`;
    }

    /**
     * Adds content to the modal
     * 
     * @param {object} A plain javascript object containing content for the modal
     * @param {selector} selector - A selector for the modal to add this content to
     */
    addModalContent(content, selector) {
        Analytics.sendEvent(null, Analytics.events.place, Analytics.actions.opened, `${content.name}  ${content.formattedAddress}`);
        let $modal = $(selector);
        let today = new Date();
        let headerImage = content.placesData.photos ? content.placesData.photos[0].getUrl({ 'maxWidth': 500 }) : Config.DEFAULT_PROVIDER_IMAGE;
        var storeHours = ``;
        var hours = '';
        if (content.placesData.opening_hours) {
            storeHours = `<li class="hours">${content.placesData.opening_hours.weekday_text.join('</li><li>')}</li>`;
            hours = content.placesData.opening_hours.open_now ? content.placesData.opening_hours.weekday_text[Utils.getDateIndex()] : 'Closed now';
        } else  {
            Analytics.sendEvent(null, Analytics.events.place, Analytics.actions.error, `${Analytics.errors.dataNotAvailable} for ${content.name}  ${content.formattedAddress}`);
        }
        
        $modal.find('.provider-image').css('background-image', `url(${headerImage})`);
        $modal.find('.modal-body').html(this.bodyHtml);
        $modal.find('.provider-name').html(`${content.name}`);
        $modal.find('.address-details').html(content.formattedAddress);
        $('#opening-hours').append(`<div class ="todays-hours">${hours}</div>`);
        $('#opening-hours').append(`<ul class="hours-list">${storeHours}</ul>`);
        if (content.website) {
            $modal.find('.address-details').append(`<div class="store-site"><a href="${content.website}" target="_blank">${content.website}</a></div>`);
        }
        $modal.find('.address-details').append(`<div><a href="tel:+${content.phoneNumber.replace(/\D+/g, '')}">${content.phoneNumber}</a></div>`);
        if (content.drug) {
            $modal.find('.brand-name-prices').html(Utils.drugPriceHtml([], content.drugPrice, content.drug, content.drugStrength));
            $modal.find('.generic-prices').html(Utils.drugPriceHtml([], content.genericPrice, Utils.i18n('genericEquivalent'), content.drugStrength));
            $modal.find('.dispensing-fee').text(Utils.i18n('dispensingFee', content.dispensingFee));
        }
        $modal.find('.modal-footer').html(this.footerHtml);
        $modal.find('.footer-confirm').text(Utils.i18n('modalConfirmation', 'OK'));
        if (content.preferred === 'preferred') {
            this.addButton('button-row', 'pp-button', `<div class="provider-icon preferred icon"><div class="button-text"></div></div>`);
        }
        if (content.specialAuthority) {
            this.addButton('button-row', 'sa-button', `<div class="special-authority icon"><div class="button-text"></div></div>`);
        }

        this.addModalListeners(selector);
        this.addPopup('pp-button', this.ppButtonContent);
        this.addPopup('sa-button', this.saButtonContent);
    }

    /**
     * Adds listeners to the modal
     * 
     * @param {selector} selector - Selector for the modal
     */
    addModalListeners(selector) {
        var $modal = $(selector);
        var $openingHours = $('#opening-hours');

        $modal.find('.button.footer-confirm').on('click', () => {
            $modal.modal('hide');
        });

        $openingHours.find('.todays-hours').on('click', this.toggleHours);
        $openingHours.find('.hours-list').on('click', this.toggleHours);

        $('#buyGeneric').hover(this.toggleHighlightState.bind(this));
        $('#buyGeneric').on('click', () => {
            window.open(Config.BUY_GENERIC);
        });
    }

    /**
     * Adds a pc-button to the specified target
     * 
     * @param {string} targetId - string for the id of the target to append the button to. Must be an id
     * @param {string} buttonId - id to attach to the button
     * @param {string} content - string of content to add to the button. Can be html or plain text.
     * @returns {jQuery Object} the created button
     */
    addButton(targetId, buttonId, content) {
        $(`#${targetId}`).append(Utils.generateButtonHtml(buttonId, content));
        $(`#${buttonId}`).hover(this.toggleHighlightState.bind(this));
        return $(`#${buttonId}`);
    }

    /**
     * Toggles the highlight state of the event target.
     */
    toggleHighlightState() {
        $(event.currentTarget).toggleClass('highlight');
        $(event.currentTarget).find('.icon').toggleClass('highlight');
    }

    /**
     * Adds a popup (popover) to the dom above this target element. This is specific to targets in modals.
     * 
     * @param {string} id - the id of the target
     * @param {string} content - the content of the pop up. Can be either html or plain text
     * @return {jQuery object} the created popover
     */
    addPopup(targetId, content) {
        let $target = $(`#${targetId}`);
        $target.popover({
            container: '.modal-content',
            content: `
                <div class="po-content">
                    ${content}
                </div>
                `,
            placement: 'auto',
            html: true,
            trigger: 'manual',
            viewport: '.sticky-content'
        });

        $target.on('click', ev => {
            let trueLeftOffset = $target.offset().left - $('#detailsModal').offset().left;
            $target.popover('show');
            $target.focus();
        });

        $target.on('blur', ev => {
            /* 
                Timeout required for Windows native touch devices as without it the link will be removed from the DOM before
                the event actually fires because it's checking for right click holds.
            */
            setTimeout(() => {
                $target.popover('hide');
            }, 50);
        });

        return $('.modal-content').find('.popover');
    }

    /**
     * Toggles the extended view of opening hours for a provider in the modal
     */
    toggleHours() {
        Analytics.sendEvent(null, Analytics.events.place, Analytics.actions.opened, 'Hours');
        var $openingHours = $('#opening-hours');
        $openingHours.find('.todays-hours').toggle();
        $openingHours.find('.hours-list').fadeToggle(250);
    }
}

/**
 * Simple modal for displaying errors
 */
export class ErrorModal extends Modal {
    constructor(options = {}) {
        super();
        this.id = "errorModal";
        this.dismissible = false;
        this.footerHtml = `<div class="button close-button">${Utils.i18n('modalConfirmation', 'OK')}</div>`;
    }
}



// WEBPACK FOOTER //
// ../D:/builds/ACES/Pharmacy Compass Production Support/PharmacyCompass2/Content/scripts/Modal.js